import type { Equal } from '$types/extensions';
import { logError } from '$lib/log_error';
import { AccountGroupId, assertUnreachable } from '@df/common';
import { accountGroupName, auditAccountGroupName } from './codegen/audit_account_groups';
import { joinWithAnd } from './textUtils';
import type { AuditActivityNode } from './codegen/activity_status_key';
import { err, ok, type Result } from 'neverthrow';

const specialGroups = ['Administration', 'Transactions', 'CustomAccounts'] as const;
type SpecialGroup = (typeof specialGroups)[number];
function isSpecial(groupId: string): groupId is SpecialGroup {
  return specialGroups.indexOf(groupId as never) !== -1;
}

export class AccountGroup implements Equal<AccountGroup> {
  constructor(private _groupId: string) {
    // TODO: temporary guard to surface the call site passing undefined — remove once identified
    if (typeof _groupId !== 'string') {
      logError(_groupId, 'AccountGroup constructor received non-string groupId');
      this._groupId = 'CustomAccounts';
      return;
    }
    if (!_groupId.endsWith('XX') && !isSpecial(_groupId)) {
      logError(_groupId, 'incorrect groupId?');
    }
  }

  toJSON(): string {
    return this._groupId;
  }

  public isSpecial(): boolean {
    return isSpecial(this._groupId);
  }

  public get auditAccountGroupName(): string | undefined {
    if (this.isSpecial()) {
      return this.display();
    }

    try {
      return auditAccountGroupName(this._groupId.slice(1));
    } catch {
      return;
    }
  }

  public get accountGroupName(): string | undefined {
    if (this.isSpecial()) {
      return this.display();
    }

    try {
      return accountGroupName(this._groupId.slice(1));
    } catch {
      return;
    }
  }

  public equals(other: AccountGroup): boolean {
    return this._groupId === other?.stringId();
  }

  public isBalanceGroup(): boolean {
    const slicedGroup = this._groupId.slice(1, 2);
    const groupNumber = parseInt(slicedGroup);
    if (groupNumber < 3) {
      return true;
    } else {
      return false;
    }
  }

  public display(): string {
    if (isSpecial(this._groupId)) {
      switch (this._groupId) {
        case 'Administration':
          return 'Förvaltning';
        case 'Transactions':
          return 'Transaktioner';
        case 'CustomAccounts':
          return 'XXXX';
        default:
          assertUnreachable(this._groupId);
      }
    }
    return this._groupId.replace('_', '');
  }

  public groupId(): AccountGroupId {
    return new AccountGroupId(this._groupId);
  }

  /**
   *
   * @returns Two digit number for AccountGroup
   */
  public groupNumber(): number {
    if (isSpecial(this._groupId)) {
      switch (this._groupId) {
        case 'Administration':
          return 90;
        case 'Transactions':
          return 91;
        case 'CustomAccounts':
          return 92;
      }
    }
    // If group is 4XXX for example
    if (this._groupId.match(/X/g)?.length === 3) {
      return parseInt(this._groupId.replace('_', '').replace('XXX', '0'));
    }
    return parseInt(this._groupId.replace('_', ''));
  }

  static sortAccountGroups(groups: AccountGroup[]): AccountGroup[] {
    return groups.sort((a, b) => a.groupNumber() - b.groupNumber());
  }

  static categorizeGroups(groups: AccountGroup[]): {
    title: string;
    data: AccountGroup[];
  }[] {
    const assets: AccountGroup[] = [];
    const liabilities: AccountGroup[] = [];
    const result: AccountGroup[] = [];

    for (const group of groups) {
      if (group.isSpecial()) continue;
      const num = group.groupNumber();
      if (num < 20) {
        assets.push(group);
      } else if (num < 30) {
        liabilities.push(group);
      } else {
        result.push(group);
      }
    }

    return [
      { title: 'Tillgångar', data: assets },
      { title: 'Skulder', data: liabilities },
      { title: 'Resultat', data: result },
    ];
  }

  static displayGroupsShort(groups: AccountGroup[]): string {
    return groups.map((group) => group.display().slice(0, 2)).join(', ');
  }

  static displayGroupsLong(groups: AccountGroup[]): string {
    return groups.map((group) => group.display()).join(', ');
  }

  static joinWithAnd(groups: AccountGroup[]): string {
    return joinWithAnd(groups.map((group) => group.display()));
  }

  static fromAccountNumber(accountNumber: string): AccountGroup {
    if (accountNumber.length !== 4) {
      throw new Error('Expected account length of 4: ' + accountNumber);
    }

    const groupId = AccountGroup.accountNumberToGroupId(accountNumber);

    return AccountGroup.fromGroupId(groupId);
  }

  static fromGroupId(groupId: string): AccountGroup {
    return new AccountGroup(groupId);
  }

  static fromAuditActivityNode(node: AuditActivityNode): Result<AccountGroup, string> {
    if (!node.startsWith('AP')) {
      return err(`Node must be AccountGroup node: ${node}`);
    }
    const groupId = node.replace('AP', '_');

    return ok(new AccountGroup(groupId));
  }

  static accountNumberToGroupId(accountNumber: string): string {
    const number = parseInt(accountNumber);
    switch (true) {
      case number < 1000:
        return 'CustomAccounts';
      case number < 1100:
        return '_10XX';
      case number < 1200:
        return '_11XX';
      case number < 1300:
        return '_12XX';
      case number < 1400:
        return '_13XX';
      case number < 1500:
        return '_14XX';
      case number < 1600:
        return '_15XX';
      case number < 1700:
        return '_16XX';
      case number < 1800:
        return '_17XX';
      case number < 1900:
        return '_18XX';
      case number < 2000:
        return '_19XX';
      case number < 2100:
        return '_20XX';
      case number < 2200:
        return '_21XX';
      case number < 2300:
        return '_22XX';
      case number < 2400:
        return '_23XX';
      case number < 2500:
        return '_24XX';
      case number < 2600:
        return '_25XX';
      case number < 2700:
        return '_26XX';
      case number < 2800:
        return '_27XX';
      case number < 2900:
        return '_28XX';
      case number < 3000:
        return '_29XX';
      case number < 3900:
        return '_30XX';
      case number < 4000:
        return '_39XX';
      case number < 5000:
        return '_4XXX';
      case number < 6000:
        return '_5XXX';
      case number < 7000:
        return '_6XXX';
      case number < 7700:
        return '_7XXX';
      case number < 8000:
        return '_78XX';
      case number < 8800:
        return '_80XX';
      case number < 8900:
        return '_88XX';
      case number < 9000:
        return '_89XX';
      default:
        return 'CustomAccounts';
    }
  }

  stringId(): string {
    return this._groupId;
  }
}

AccountGroup.prototype.toString = AccountGroup.prototype.stringId;

export const splitAccountGroups = (groups: string[]): [AccountGroup[], AccountGroup[]] =>
  groups.reduce(
    ([prevBalance, prevResult], group) => {
      const accountGroup = AccountGroup.fromGroupId(group);
      if (accountGroup.isSpecial()) {
        return [prevBalance, prevResult];
      }
      if (accountGroup.isBalanceGroup()) {
        prevBalance.push(accountGroup);
      } else {
        prevResult.push(accountGroup);
      }

      return [prevBalance, prevResult];
    },
    [[], []] as [AccountGroup[], AccountGroup[]],
  );
